import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { RequireAdmin } from "@/components/shared/require-admin";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { FormError } from "@/components/shared/form-error";
import {
  useTeams,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  type Team,
} from "@/hooks/use-teams";

const teamFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
});

type TeamFormValues = z.infer<typeof teamFormSchema>;

function TeamsTableSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading teams">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">Created</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-8 w-20" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TeamFormDialog({
  open,
  onOpenChange,
  team,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team: Team | null;
}) {
  const isEditing = team !== null;
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam(team?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: team ? { name: team.name } : { name: "" },
  });

  useEffect(() => {
    reset(team ? { name: team.name } : { name: "" });
  }, [team, reset]);

  const isPending = createTeam.isPending || updateTeam.isPending;

  const onSubmit = (data: TeamFormValues) => {
    if (isEditing) {
      updateTeam.mutate(data, {
        onSuccess: () => {
          onOpenChange(false);
          reset();
        },
      });
    } else {
      createTeam.mutate(data, {
        onSuccess: () => {
          onOpenChange(false);
          reset();
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Team" : "Create Team"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the team name."
              : "Add a new team to the organization."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name</Label>
            <Input
              id="team-name"
              placeholder="e.g., Infrastructure & Operations"
              {...register("name")}
              autoFocus
            />
            <FormError message={errors.name?.message} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// DeleteTeamDialog replaced by DeleteConfirmDialog (inline at usage site)

export function AdminTeamsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const deleteTeam = useDeleteTeam();

  const { data, isLoading, error } = useTeams();
  const teams = data?.data ?? [];
  const nextCursor = data?.next_cursor ?? null;

  const handleEdit = (team: Team) => {
    setSelectedTeam(team);
    setFormOpen(true);
  };

  const handleDeleteClick = (team: Team) => {
    setSelectedTeam(team);
    setDeleteOpen(true);
  };

  const handleCreate = () => {
    setSelectedTeam(null);
    setFormOpen(true);
  };

  return (
    <RequireAdmin>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-heading text-2xl font-semibold sm:text-3xl">Team Management</h1>
          <Button onClick={handleCreate} aria-label="Create new team">
            <Plus className="mr-2 h-4 w-4" />
            New Team
          </Button>
        </div>

        {isLoading ? (
          <TeamsTableSkeleton />
        ) : error ? (
          <EmptyState
            title="Failed to load teams"
            description={error.message}
          />
        ) : teams.length === 0 ? (
          <EmptyState
            title="No teams found"
            description="Create your first team to get started."
            actionLabel="New Team"
            onAction={handleCreate}
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Created</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => (
                    <TableRow key={team.id}>
                      <TableCell className="font-medium">{team.name}</TableCell>
                      <TableCell className="hidden sm:table-cell text-[hsl(var(--muted-foreground))]">
                        {new Date(team.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(team)}
                            aria-label={`Edit ${team.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(team)}
                            aria-label={`Delete ${team.name}`}
                          >
                            <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {nextCursor && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={() => { /* pagination handled by parent */ }}>
                  Load More
                </Button>
              </div>
            )}
          </>
        )}

        <TeamFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          team={selectedTeam}
        />

        <DeleteConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Team"
          description={<>Are you sure you want to delete <strong>{selectedTeam?.name}</strong>? Users assigned to this team will need to be reassigned.</>}
          onConfirm={() => { if (selectedTeam) deleteTeam.mutate(selectedTeam.id, { onSuccess: () => setDeleteOpen(false) }); }}
          isPending={deleteTeam.isPending}
        />
      </div>
    </RequireAdmin>
  );
}
